<template>
  <div :class="['collapsable-section', { 'collapsable-section--separator': separator }]">
    <div class="collapsable-section__title">
      <button
        v-if="canBeCollapsable"
        class="collapsable-section__title__button"
        @click="updateIsCollapsed(!isCollapsed)"
      >
        <slot name="title">
          <div class="title">
            {{ title }}
          </div>
        </slot>

        <div>
          <ArrowIcon
            :size="24"
            variant="dark"
            :direction="isCollapsed ? 'right' : 'bottom'"
          />
        </div>
      </button>

      <div v-else>
        <slot name="title">
          <div class="title">
            {{ title }}
          </div>
        </slot>
      </div>
    </div>
    <div class="collapsable-section__content">
      <div
        class="collapsable-section__content__html-text"
        :class="{ expanded: !isCollapsed }"
      >
        <div
          class="v-html-target"
          v-html="sectionContent"
        />
        <div
          v-if="isCollapsed && isCollapsable && canBeCollapsable"
          class="collapsable-section__content__html-text__mist"
        />
      </div>
      <span
        v-if="isCollapsed && canBeCollapsable"
        style="display: inline-block"
      >...</span>
      <div><slot /></div>
      <button
        v-if="canBeCollapsable"
        class="collapsable-section__button"
        @click="updateIsCollapsed(!isCollapsed)"
      >
        <Lang
          v-if="isCollapsed"
          by-key="see-more"
          capitalize-first
        />
        <Lang
          v-else
          by-key="see-less"
          capitalize-first
        />
      </button>
    </div>
  </div>
</template>

<script lang="ts">
import { useMobileDevice } from '@/core/composable/shared/useMobileDevice'
import ArrowIcon from './icons/Arrow.icon.vue'

export default defineNuxtComponent({
  components: {
    ArrowIcon
  },
  props: {
    title: {
      type: String,
      default:  () => '',
    },
    isCollapsable: {
      type: Boolean,
      default: false,
    },
    large: {
      type: Boolean,
      default: false,
    },
    startCollapsed: {
      type: Boolean,
      default: false,
    },
    columns: {
      type: Number,
      default: 1,
    },
    separator: {
      type: Boolean,
      default: true,
    },
    html: {
      type: String,
      default: () => '',
    },
  },
  setup(props) {
    const { isMobile } = useMobileDevice()
    const textLength = computed(() => (isMobile.value ? 210 : props.large ? 1400 : 400))

    const canBeCollapsable = computed(() => {
      if (props.html.length <= textLength.value) {
        return false
      }

      return props.isCollapsable
    })

    const sectionContent = props.html
      .replaceAll('\r', '')
      .replaceAll('\n', '<br>')
      .replaceAll('<br><br><br>', '<br><br>')
      .replaceAll('  ', ' ')
      .replaceAll(' >', '>')
      .trim()

    const isCollapsed = ref(!canBeCollapsable.value ? false : props.startCollapsed)
    const collapsable = computed(() => props.isCollapsable)

    const updateIsCollapsed = (value: boolean) => {
      isCollapsed.value = value
    }

    return {
      isCollapsed,
      updateIsCollapsed,
      canBeCollapsable,
      collapsable,

      sectionContent,
    }
  },
})
</script>

<style lang="scss">
.collapsable-section {
  @screen lg {
    @apply pb-2;
  }

  &__title {
    @apply flex;
    @apply justify-between;
    @apply mb-4;
    @apply pb-2;
    @apply mt-3;
    @apply text-lg;
    @apply font-bold text-gray-800;

    @screen lg {
      @apply mt-0 mb-3;
      @apply text-xl;
    }

    &__button {
      @apply cursor-pointer;
      @apply block w-full;

      @apply flex justify-between items-center;
    }
  }
  &__content {
    @apply mb-4;

    &__html-text {
      @apply relative;
      @apply text-base;
      white-space: pre-wrap;

      text-overflow: ellipsis;
      overflow: hidden;

      max-height: 200px;

      &__mist {
        @apply w-full;
        height: 24px;

        @apply absolute;
        bottom: 0px;

        background: rgb(255,255,255);
        background: linear-gradient(180deg, rgba(255,255,255,0) 0%, rgba(255,255,255,1) 100%);
      }
    }

    &__html-text.expanded {
      max-height: unset;
      height: auto;
    }
  }
  &--separator {
    @apply border-b;
    @apply border-solid;
    @apply border-gray-300;
  }

  &__button {
    @apply text-base;
    @apply underline;
  }
}
</style>
